import { FC, useEffect, useState } from 'react';

import { useNavigate, Link } from 'react-router-dom';

import spot from 'data/spot.json';

import { useGetArticleData } from 'hooks/useGetArticleData';
import { useGetProgressData } from 'hooks/useGetProgressData';
import { useNews } from 'hooks/useNews';
import { usePageTitle } from 'hooks/usePageTitle';

import { CompletedArea } from 'components/features/CompletedArea';
import { Button } from 'components/parts/Button';
import { Paragraph } from 'components/parts/Paragraph';
import { TOTAL_MISSIONS, SPECIAL_AREA_MISSION_IDS } from 'constants/mission';
import IconClearCard from 'images/icon/clear-card.svg';
import IconMissionCard from 'images/icon/mission-card.svg';
import InfoAlert from 'images/icon/info_alert.svg';
import IconPrize from 'images/icon/prizes.svg';
import FlagImage from './images/flag.webp';
import { SpotData, ProgressResponse } from 'types/spot';
import { isLatestNews } from 'utils/isLatestNews';

import styles from './Home.module.scss';
import { ClearStampsRow } from 'components/pages/Home/components/ClearStampsRow/ClearStampsRow';
import { cx } from 'utils/cx';

function numberInRange(start: number, end: number) {
  return Array.from({ length: end - start + 1 }, (_, i) => start + i);
}

export const Home: FC = () => {
  const navigate = useNavigate();
  const { data: progressData } = useGetProgressData();
  const spotList: SpotData[] = spot as unknown as SpotData[];
  const recentlyExploredAreaNo = progressData?.recentlyExploredAreaNo;
  const recentlyExploredAreaArticle = useGetArticleData(String(recentlyExploredAreaNo) || '');
  const completedAreaList = progressData?.completedAreaList;
  const newsData = useNews(3);
  const [isLatestArray, setIsLatestArray] = useState<boolean[]>([]);

  function getCompletedArticles(
    spotList: SpotData[],
    completedAreaList: ProgressResponse['completedAreaList'] | undefined
  ): SpotData[] {
    if (completedAreaList === undefined) {
      return [];
    }

    return completedAreaList
      .map(completedId => spotList.find(spot => spot.id === completedId.toString()))
      .filter((article): article is SpotData => article !== undefined);
  }
  const completedArticles: SpotData[] = getCompletedArticles(spotList, completedAreaList);

  function getClearStampFileName(id: string) {
    return `${String(id).padStart(3, '0')}_clear-stamp.png`;
  }
  const isHalfwayClear = Boolean(completedAreaList?.length && completedAreaList.length >= 5);
  const isAlmostClear = Boolean(completedAreaList?.length && completedAreaList.length >= 10);
  const isAllClear = progressData?.completedAreaList.length === Number(TOTAL_MISSIONS);
  const receivedHalfway = progressData?.rewardList.includes('halfway') ?? false;
  const receivedAlmost = progressData ? progressData?.rewardList.includes('almost') : false;
  const receivedPerfect = progressData ? progressData?.rewardList.includes('perfect') : false;

  const clearStampIdsTo5 = numberInRange(0, 4).map(i => completedArticles[i]?.id) ?? [];
  const clearStampIdsTo10 = numberInRange(5, 9).map(i => completedArticles[i]?.id) ?? [];
  const clearStampIdsTo15 = numberInRange(10, 14).map(i => completedArticles[i]?.id) ?? [];
  const clearStampIdsTo19 = numberInRange(15, 18).map(i => completedArticles[i]?.id) ?? [];

  usePageTitle(`ホーム`);

  useEffect(() => {
    if (newsData) {
      setIsLatestArray(newsData.map(news => isLatestNews(news.date)));
    }
  }, [newsData]);

  return (
    <div className={styles.wrapper}>
      <img src={FlagImage} alt="Flag" className={styles.flag} />
      <div className={styles.inner}>
        <header className={styles.header}>
          <h1 className={styles.logo}>
            <img
              className={styles.logoImage}
              src={require(`images/logo.webp`)}
              alt="コードF15 ODYSSEY 幸せの風吹く空への道しるべ"
            />
          </h1>
        </header>
        <div className={styles.body}>
          <div className={styles.lead}>
            <Paragraph addClass={[styles.leadText]}>
              訪れた者にめぐみと幸せをもたらすふしぎな国「空の国」をめざすため、謎を解読して「空の国に導く宝」を探そう。
            </Paragraph>
          </div>

          {recentlyExploredAreaArticle && !isAllClear && (
            <div className={styles.recentlyExplored}>
              <Button
                text="最近探索したエリアへ"
                styleType="secondary"
                handleClick={() => navigate(`/area/${recentlyExploredAreaArticle.id}`)}
                addClass={[styles.spotButton]}
              />
            </div>
          )}

          <div className={styles.goArea}>
            <Button
              text="探しに行く！"
              styleType="primary"
              handleClick={() => navigate('/area/')}
              addClass={[styles.spotButton]}
            />
          </div>

          {/* クリアスタンプ */}
          <div className={styles.clearStamp}>
            <div className={styles.clearStampInner}>
              <h2 className={styles.clearStampHeading}>クリアスタンプ</h2>
              {/* <ol className={[styles.clearStampList, styles.clearStampListFirst].join(' ')}>
                {Array.from({ length: 5 }).map((_, i) => {
                  const index = i;
                  return (
                    <li key={index} className={styles.clearStampListItem}>
                      {completedArticles[index]?.id && (
                        <img
                          src={require(`components/pages/Area/images/${getClearStampFileName(
                            completedArticles[index].id
                          )}`)}
                          alt={`clear stamp of ${completedArticles[index].id}`}
                          className={styles.clearStampListImage}
                        />
                      )}
                    </li>
                  );
                })}
              </ol> */}
              <ClearStampsRow
                clearStampIds={clearStampIdsTo5}
                displayNumberOffset={1}
                showPrizeIconForLast
              />
              <div className={styles.clearStampButtonWrap}>
                <button
                  onClick={() => navigate('/reward/halfway')}
                  disabled={!isHalfwayClear}
                  className={styles.clearStampButton}
                >
                  {!receivedHalfway && isHalfwayClear && (
                    <img src={InfoAlert} alt="" className={styles.clearStampButtonIcon} />
                  )}
                  5エリアクリア賞
                </button>
              </div>
              {/* <ol
                start={6}
                className={[styles.clearStampList, styles.clearStampListSecond].join(' ')}
              >
                {Array.from({ length: 5 }).map((_, i) => {
                  const index = i + 5;
                  return (
                    <li key={index} className={styles.clearStampListItem}>
                      {completedArticles[index]?.id && (
                        <img
                          src={require(`components/pages/Area/images/${getClearStampFileName(
                            completedArticles[index].id
                          )}`)}
                          alt={`clear stamp of ${completedArticles[index].id}`}
                          className={styles.clearStampListImage}
                        />
                      )}
                    </li>
                  );
                })}
              </ol> */}
              <ClearStampsRow
                clearStampIds={clearStampIdsTo10}
                displayNumberOffset={6}
                showPrizeIconForLast
              />
              <div className={styles.clearStampButtonWrap}>
                <button
                  onClick={() => navigate('/reward/almost')}
                  disabled={!isAlmostClear}
                  className={styles.clearStampButton}
                >
                  {!receivedAlmost && isAlmostClear && (
                    <img src={InfoAlert} alt="" className={styles.clearStampButtonIcon} />
                  )}
                  10エリアクリア賞
                </button>
              </div>
              {/* <ol
                start={11}
                className={[styles.clearStampList, styles.clearStampListSecond].join(' ')}
              >
                {Array.from({ length: 9 }).map((_, i) => {
                  const index = i + 10;
                  return (
                    <li key={index} className={styles.clearStampListItem}>
                      {completedArticles[index]?.id && (
                        <img
                          src={require(`components/pages/Area/images/${getClearStampFileName(
                            completedArticles[index].id
                          )}`)}
                          alt={`clear stamp of ${completedArticles[index].id}`}
                          className={styles.clearStampListImage}
                        />
                      )}
                    </li>
                  );
                })}
              </ol> */}
              <ClearStampsRow
                clearStampIds={clearStampIdsTo15}
                displayNumberOffset={11}
                showPrizeIconForLast={false}
              />
              <ClearStampsRow
                clearStampIds={clearStampIdsTo19}
                displayNumberOffset={16}
                showPrizeIconForLast
              />
              <div className={styles.clearStampButtonWrap}>
                <button
                  onClick={() => navigate('/reward/perfect')}
                  disabled={!isAllClear}
                  className={styles.clearStampButton}
                >
                  {!receivedPerfect && isAllClear && (
                    <img src={InfoAlert} alt="" className={styles.clearStampButtonIcon} />
                  )}
                  パーフェクト賞
                </button>
              </div>
            </div>
          </div>

          <div className={styles.nav}>
            <div className={styles.navItem}>
              <button onClick={() => navigate('/card?type=area')} className={styles.navButton}>
                <span className={styles.navIconWrap}>
                  <img src={IconClearCard} alt="" className={styles.navIcon} />
                </span>
                <span className={styles.navText}>クリアカード</span>
              </button>
            </div>
            <div className={styles.navItem}>
              <button onClick={() => navigate('/card?type=mission')} className={styles.navButton}>
                <span className={styles.navIconWrap}>
                  <img src={IconMissionCard} alt="" className={styles.navIcon} />
                </span>
                <span className={styles.navText}>ミッションカード</span>
              </button>
            </div>
          </div>
        </div>
        <div className={styles.award}>
          <button onClick={() => navigate('/prizes')} className={cx(styles.navButton)}>
            <span className={styles.navIconWrap}>
              <img src={IconPrize} alt="" className={styles.navIcon} />
            </span>
            <span className={styles.navText}>抽選応募賞品</span>
          </button>
          <p className={styles.awardText}>
            クリアしたエリアの合計数が多いほど当選対象の賞品が増えるよ！
            <br />
            また、全部で9エリアある「浜通りエリア」を全てクリアすると「浜通り賞」にも応募できるよ！
          </p>
          <CompletedArea
            placeholder="プレゼント応募ランク"
            totalAreas={TOTAL_MISSIONS}
            completedAreas={progressData?.completedAreaList.length || 0}
            isPrize={true}
          />
          <CompletedArea
            placeholder="浜通り賞"
            totalAreas={SPECIAL_AREA_MISSION_IDS.length}
            completedAreas={
              progressData?.completedAreaList.filter(completedId =>
                SPECIAL_AREA_MISSION_IDS.some(specialId => String(completedId) === specialId)
              ).length || 0
            }
            isPrize={false}
            withAreaUnit={true}
            altBarColor
          />
        </div>
        <div className={styles.footer}>
          <div className={styles.news}>
            <h2 className={styles.newsHeading}>お知らせ</h2>
            {newsData && (
              <ol className={styles.newsList}>
                {newsData.map((news, index) => {
                  return (
                    <li key={index} className={styles.newsListItem}>
                      <Link to={`/menu/news#${news.number}`} className={styles.newsListLink}>
                        <div className={styles.newsListHead}>
                          <p className={styles.newsListDate}>{news.date}</p>
                          {isLatestArray[index] && <p className={styles.newsListLabel}>NEW</p>}
                        </div>
                        <div className={styles.newsListBody}>
                          <p className={styles.newsListTitle}>{news.title}</p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ol>
            )}
            <div className={styles.newsFoot}>
              <Button
                text="お知らせ一覧"
                styleType="secondary"
                handleClick={() => navigate('/menu/news/')}
                addClass={[styles.spotButton]}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
